import React, { useRef, useState } from "react";
import { X, ImageUp, SquarePen, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import transparentBg from "../assets/images/transparent-bg.png";
import { PatientService } from "../services/api/patient.service";

interface PatientFamilyDetails {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  relation: string;
  dateOfBirth: string;
  age: string | { years: number; months: number; days: number };
  gender: "Male" | "Female" | "Other";
  mobileNumber: string;
  email: string;
  address: string;
  profileImage?: File | string;
  pincode: string;
}

export function AddNewFamilyMember({
  onClose,
  onSuccess,
  patientId,
  mobileNumber,
}: {
  onClose: () => void;
  onSuccess: (familyMemberData: PatientFamilyDetails) => void;
  patientId?: string;
  mobileNumber?: string;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef<HTMLLabelElement>(null);
  const [patientData, setPatientData] = useState<PatientFamilyDetails>({
    id: "",
    firstName: "",
    lastName: "",
    name: "",
    relation: "",
    dateOfBirth: "",
    age: "",
    gender: "Male",
    mobileNumber: "",
    email: "",
    address: "",
    profileImage: "",
    pincode: "",
  });

  // Age calculation and formatting functions (keep from previous component)
  const calculateAge = (dob: string) => {
    if (!dob) return { years: 0, months: 0, days: 0 };

    const birthDate = new Date(dob);
    const today = new Date();

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      const lastMonthDays = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
      days += lastMonthDays;
      months--;
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    if (years > 0) {
      return `${years} yrs ${months} months`;
    } else if (months > 0) {
      return `${months} months`;
    } else {
      return `${days} days`;
    }
  };

  const formatAgeString = (
    age: { years: number; months: number; days: number } | string | null
  ) => {
    if (!age) return "N/A";
    if (typeof age === "string") return age;

    const { years, months, days } = age;
    return years > 0
      ? `${years} yrs ${months} months`
      : months > 0
      ? `${months} months`
      : `${days} days`;
  };

  // Handlers for form inputs
  const handleDOBChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dob = e.target.value;
    const ageData = calculateAge(dob);

    setPatientData((prev) => ({
      ...prev,
      dateOfBirth: dob,
      age: ageData,
    }));
  };

  const formatDateOfBirth = (dob: string) => {
    return dob ? new Date(dob).toISOString().split("T")[0] : "";
  };

  // Split fullName for editing mode
  const splitFullName = (fullName: string) => {
    if (!fullName) return { firstName: "", lastName: "" };
    
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    
    return { firstName, lastName };
  };

  // Combine firstName and lastName to create fullName
  const getFullName = () => {
    return `${patientData.firstName} ${patientData.lastName}`.trim();
  };

  // Use effect to update patientData.name when firstName or lastName changes
  React.useEffect(() => {
    setPatientData(prev => ({
      ...prev,
      name: getFullName()
    }));
  }, [patientData.firstName, patientData.lastName]);

  // Use effect to split name when editing
  React.useEffect(() => {
    if (patientData.name && (!patientData.firstName && !patientData.lastName)) {
      const { firstName, lastName } = splitFullName(patientData.name);
      setPatientData(prev => ({
        ...prev,
        firstName,
        lastName
      }));
    }
  }, []);

  // Validation function
  const validateForm = (): string | true => {
    if (patientData.firstName.trim().length === 0) {
      return "First Name is required";
    }
    if (patientData.lastName.trim().length === 0) {
      return "Last Name is required";
    }
    if (patientData.relation.trim().length === 0) {
      return "Relation is required";
    }
    if (patientData.dateOfBirth.trim().length === 0) {
      return "Date of Birth is required";
    }
    if (patientData.gender.trim().length === 0) {
      return "Gender is required";
    }
    if (patientData.address.trim().length === 0) {
      return "Address is required";
    }
    if (patientData.pincode.trim().length === 0) {
      return "Pincode is required";
    }
    if (patientData.pincode.length < 6) {
      return "Invalid Pincode";
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationResult = validateForm();
    if (validationResult !== true) {
      toast.error(validationResult);
      return;
    }

    try {
      const isoDateOfBirth = new Date(patientData.dateOfBirth).toISOString();
      // Prepare form data for submission
      const formData = new FormData();
      formData.append("patient_id", patientId as string);
      formData.append("mobile_number", mobileNumber as string);
      formData.append("name", patientData.name); // Using the combined name
      formData.append("relation", patientData.relation);
      formData.append("date_of_birth", isoDateOfBirth);
      formData.append("age", formatAgeString(patientData.age));
      formData.append("gender", patientData.gender);
      formData.append("email", patientData.email);
      formData.append("address", patientData.address);
      formData.append("pincode", patientData.pincode);
      formData.append("status", 'Active');

      if (patientData.profileImage instanceof File) {
        formData.append("profileImage", patientData.profileImage);
      }

      const response = await PatientService.addNewPatientFamily(formData);
      if (response.success) {
        toast.success("Family member added successfully!");
        onSuccess({
          ...response.data
        });
        onClose();
      } else {
        toast.error(response.message || "Failed to add family member");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };

  // Image upload handlers
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      setPatientData((prev) => ({
        ...prev,
        profileImage: file,
      }));
    }
  };

  const handleDeletePhoto = () => {
    setPatientData((prev) => ({
      ...prev,
      profileImage: "",
    }));
  };

  // Drag and drop image handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith("image/")
      );
      if (droppedFiles.length > 0) {
        setPatientData((prev) => ({
          ...prev,
          profileImage: droppedFiles[0],
        }));
      } else {
        toast.error("Please drop only image files");
      }
      e.dataTransfer.clearData();
    }
  };

  return (
    <div className="hiddenScrollbar absolute z-20 inset-0 pb-4" style={{ height: "129vh" }}>
      <div
        className="hiddenScrollbar absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${transparentBg})`,
          height: "inherit",
        }}
      />
      <div className="hiddenScrollbar relative w-full h-full flex items-start justify-center overflow-auto">
        <div className="bg-white rounded-xl w-[650px] shadow-xl my-5">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-medium text-emerald-600">
              ADD NEW FAMILY MEMBER
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div className="space-y-4">
              {/* Profile Image Upload */}
              {patientData.profileImage && (
                <div className="flex flex-col items-center justify-center">
                  <label
                    ref={dropZoneRef}
                    htmlFor="family-member-image"
                    className={`relative w-24 h-24 mb-2 rounded-full border border-gray-300 flex items-center justify-center bg-gray-50 cursor-pointer hover:bg-gray-100 overflow-hidden ${
                      isDragging
                        ? "border-2 border-dashed border-emerald-500 bg-emerald-50"
                        : ""
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      id="family-member-image"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />

                    {patientData.profileImage ? (
                      <img
                        src={
                          patientData.profileImage instanceof File
                            ? URL.createObjectURL(patientData.profileImage)
                            : patientData.profileImage
                        }
                        alt="Family Member"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-400 text-sm">
                        Upload Image
                      </span>
                    )}
                  </label>

                  <div className="flex gap-3">
                    <label
                      htmlFor="family-member-image"
                      className="cursor-pointer"
                    >
                      <SquarePen className="w-5 h-5 text-gray-600" />
                    </label>

                    <button
                      onClick={handleDeletePhoto}
                      type="button"
                      className="text-sm text-[#FF6C6C]"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {!patientData.profileImage && (
                <label
                  htmlFor="family-member-image"
                  className={`cursor-pointer rounded-md flex items-center flex-col justify-center bg-gray-100 py-2 ${
                    isDragging
                      ? "border-2 border-dashed border-emerald-500 bg-emerald-50"
                      : ""
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="family-member-image"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <label
                    htmlFor="family-member-image"
                    className="w-11 h-11 mx-auto mb-2 rounded-full bg-emerald-600 flex items-center justify-center hover:bg-emerald-600/70"
                  >
                    <ImageUp className="w-5 h-5 text-white" />
                  </label>
                  <p className="text-xs text-gray-500">
                    Click to upload or drag and drop your photo
                  </p>
                  <p className="text-xs text-gray-500">
                    Upload photo (optional)
                  </p>
                </label>
              )}

              {/* Name Input - Replaced with First Name and Last Name */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-sm text-gray-600 font-medium">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={patientData.firstName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[A-Za-z]*$/.test(value)) { // No spaces allowed
                        setPatientData((prev) => ({
                          ...prev,
                          firstName: value,
                        }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                    placeholder="Enter first name"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm text-gray-600 font-medium">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={patientData.lastName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[A-Za-z]*$/.test(value)) { // No spaces allowed
                        setPatientData((prev) => ({
                          ...prev,
                          lastName: value,
                        }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              {/* Relation Input */}
              <div className="space-y-2">
                <label className="block text-sm text-gray-600 font-medium">
                  Relation
                </label>
                <input
                  type="text"
                  value={patientData.relation}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      setPatientData((prev) => ({
                        ...prev,
                        relation: value,
                      }));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter relation (e.g., Brother, Sister)"
                />
              </div>

              {/* Date of Birth and Gender */}
              <div className="grid gap-4">
                <div className="space-y-2 relative">
                  <label className="block text-sm text-gray-600 font-medium">
                    Date of Birth (Age)
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      value={formatDateOfBirth(patientData.dateOfBirth)}
                      onChange={handleDOBChange}
                      onKeyDown={(e) => e.preventDefault()}
                      className="w-full px-3 py-2 pr-28 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 appearance-none"
                      style={{
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        appearance: "none",
                        backgroundImage: "none",
                        backgroundColor: "transparent",
                        position: "relative",
                      }}
                    />
                    {patientData.dateOfBirth && (
                      <span className="absolute inset-y-0 right-1 flex items-center text-gray-700 text-sm pointer-events-none">
                        ({formatAgeString(patientData.age)})
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-gray-600 font-medium">
                    Gender
                  </label>
                  <select
                    value={patientData.gender}
                    onChange={(e) =>
                      setPatientData((prev) => ({
                        ...prev,
                        gender: e.target.value as PatientFamilyDetails["gender"],
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Email Input */}
              <div className="space-y-2">
                <label className="block text-sm text-gray-600 font-medium">
                  Email
                </label>
                <input
                  type="email"
                  value={patientData.email}
                  onChange={(e) =>
                    setPatientData((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter email address"
                />
              </div>

              {/* Address Input */}
              <div className="space-y-2">
                <label className="block text-sm text-gray-600 font-medium">
                  Address
                </label>
                <input
                  type="text"
                  value={patientData.address}
                  onChange={(e) =>
                    setPatientData((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter address"
                />
              </div>

              {/* City, State, Pincode */}
              <div className="grid grid-cols-1 gap-3 mb-4">
                <div className="space-y-2">
                  <label className="block text-sm text-gray-600 font-medium">
                    Pincode
                  </label>
                  <input
                    type="text"
                    value={patientData.pincode}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 6 && /^\d*$/.test(value)) {
                        setPatientData((prev) => ({
                          ...prev,
                          pincode: value,
                        }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                    placeholder="Enter pincode"
                  />
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-4 py-2 text-sm font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
              >
                Add Family Member
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddNewFamilyMember;